import React, { useState, useEffect } from 'react';
import Lightbox from './Lightbox';
import { imageUrl } from '../lib/images';

// Fallback data in case of offline preview
const FALLBACK_GALLERIES = {
  industrigods: {
    titel: 'Industrigods',
    beskrivelse: 'Langt største delen af de fragtopgaver vi gennefører er industrigods af enhver art som køres fra firma til firma.',
    billeder: [
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-stykgods.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-t-nder-p-palle.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-landbrugsmaskiner.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-trykkerimaskiner.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/pallegods-i-trailer-3.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-sanwich-plader.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-vindm-ller.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/containerfragt-af-skovmaskiner-1.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-tr-stammer.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-redningsudstyr-1.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/pallegods-i-container.jpg'
    ]
  },
  'emballering-maskine': {
    titel: 'Hvordan man emballerer en lille maskine på palle',
    beskrivelse: 'Maskinen sendes på 4 euro paller der er sammenbygget, hvilket giver et areal på 240 x 160 cm. Billedserien er tilsendt af afsender - Konesilta, Græsslåmaskinen er godt beskyttet og kan håndteres med truck ved af-, på- og omlæsning.',
    billeder: [
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/palleteret-maskine-1-0.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/palleteret-maskine-2-0.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/palleteret-maskine-3.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/palleteret-maskine-4.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/palleteret-maskine-5.jpg'
    ]
  },
  'dinosaur-transport': {
    titel: 'Dinosaur Model til Zoologisk Museum i Zürich',
    beskrivelse: '2020 havde vi fornøjelsen at transportere en dinosaur model med tilbehør til Zoologisk Museum i Zürich.',
    billeder: [
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/lastbil-med-ben-side-0.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/dinosaur-p-manitou-1.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/dinosaur-through-door.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/dinosaur-in-museum.jpg',
      'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-dinosaurus-p-nedbygget-trailer.jpg'
    ]
  }
};

export default function Gallery({ slug, initialImages = null, title = '', columns = 3, showTitle = false }) {
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(Boolean(slug));
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (!slug) {
      if (initialImages) {
        setGalleryData({ billeder: initialImages, titel: title });
      }
      return;
    }

    let isMounted = true;
    async function fetchGallery() {
      setLoading(true);
      try {
        const res = await fetch(`/_api/public/content/galleri`);
        if (res.ok) {
          const items = await res.json();
          const list = Array.isArray(items) ? items : items?.items || [];
          const match = list.find(
            (g) => g.slug === slug || g.slug?._ === slug || (typeof g.slug === 'object' && Object.values(g.slug).includes(slug))
          );
          if (match && isMounted) {
            setGalleryData(match);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('Could not fetch gallery from CMS endpoint, using fallback', err);
      }

      if (isMounted) {
        // Fallback to static data
        setGalleryData(FALLBACK_GALLERIES[slug] || (initialImages ? { billeder: initialImages, titel: title } : null));
        setLoading(false);
      }
    }

    fetchGallery();
    return () => {
      isMounted = false;
    };
  }, [slug, initialImages, title]);

  const images = galleryData?.billeder || initialImages || [];
  if (loading) {
    return (
      <div className="py-8 flex justify-center items-center text-slate-400 text-sm">
        <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
        Henter galleri...
      </div>
    );
  }

  if (!images || images.length === 0) return null;

  return (
    <div className="my-6">
      {showTitle && galleryData?.titel && (
        <h3 className="text-xl font-bold text-[#0f2b48] mb-2">{galleryData.titel}</h3>
      )}
      {showTitle && galleryData?.beskrivelse && (
        <p className="text-sm text-slate-600 mb-4">{galleryData.beskrivelse}</p>
      )}

      {/* Grid of gallery thumbnails */}
      <div
        className={`grid gap-4 sm:gap-6 ${
          columns === 4
            ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
            : columns === 2
            ? 'grid-cols-1 sm:grid-cols-2'
            : 'grid-cols-2 sm:grid-cols-3'
        }`}
      >
        {images.map((imgUrl, idx) => {
          const urlStr = typeof imgUrl === 'string' ? imgUrl : imgUrl?.url || imgUrl?.src;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => setLightboxIndex(idx)}
              className="group relative aspect-4/3 overflow-hidden rounded-xl bg-slate-100 border border-slate-200/80 shadow-2xs hover:shadow-md transition-all text-left focus:outline-hidden focus:ring-2 focus:ring-sky-600 cursor-pointer"
              aria-label={`Vis billede ${idx + 1} i stor visning`}
            >
              <img
                src={imageUrl(urlStr, 'width=600,quality=80,format=auto')}
                alt={galleryData?.titel ? `${galleryData.titel} ${idx + 1}` : `Galleri billede ${idx + 1}`}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/30 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity p-2.5 rounded-full bg-white/90 text-slate-900 shadow-md">
                  <i className="fa-solid fa-expand text-sm" aria-hidden="true" />
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Lightbox modal */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </div>
  );
}
